import { NgClass } from '@angular/common';
import {
  Component,
  ElementRef,
  forwardRef,
  input,
  InputSignal,
  model,
  ModelSignal,
  untracked,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SkeletonLoaderComponent } from '@shared/components/ui/skeleton-loader';

type TState = 'default' | 'disabled';

@Component({
  selector: 'lib-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['./checkbox.component.scss'],
  imports: [NgClass, SkeletonLoaderComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
})
export class CheckboxComponent implements ControlValueAccessor {
  /**
   * The unique identifier of the input.
   * @default null
   */
  public readonly id: InputSignal<string> = input<string>();

  /**
   * The value of the input.
   * @default false
   */
  public readonly value: ModelSignal<boolean> = model<boolean>(false);

  /**
   * The state of the input.
   * @default 'default'
   * @type 'default' | 'disabled'
   */
  public readonly state: ModelSignal<TState> = model<TState>('default');

  /**
   * A flag indicating if the input is required.
   * @default false
   */
  public readonly required: InputSignal<boolean> = input<boolean>();

  public readonly partialSelected: ModelSignal<boolean> = model<boolean>();
  public readonly skeleton: InputSignal<boolean> = input<boolean>(false);

  private readonly nativeInput = viewChild<ElementRef<HTMLInputElement>>('input');
  /**
   * The function to be called when the input changes.
   */
  public onChange: any = () => {
    // Replaced by registerOnChange (ControlValueAccessor).
  };

  /**
   * The function to be called when the input is touched.
   */
  public onTouched: any = () => {
    // Replaced by registerOnTouched (ControlValueAccessor).
  };

  /**
   * Updates the value when the input changes.
   * @returns void
   */
  public set _value(value: boolean) {
    this.value.set(value);
    this.onChange?.(this.value());
    this.onTouched?.();
  }

  /**
   * Returns the current value of the input.
   * @returns string
   */
  public get _value(): boolean {
    return this.value();
  }

  /**
   * Handles the native checkbox `change` event.
   *
   * The input is bound with `[checked]` + `(change)` instead of `[(ngModel)]`
   * on purpose: combining `ngModel` with the `ControlValueAccessor` (used via
   * `formControlName`) creates a value feedback loop that can revert a single
   * click back to its previous state. Driving the native input explicitly keeps
   * the `value` model as the single source of truth and propagates through
   * `onChange` exactly once.
   * @param event The native change event.
   * @returns void
   */
  public onNativeChange(event: Event): void {
    this._value = (event.target as HTMLInputElement).checked;
  }

  /**
   * Handles a click on the projected content, which toggles the input.
   *
   * A `<label for>` pointing at the input already toggles it natively, so a
   * click inside one is left to the browser: toggling here too would undo it,
   * and the consumer would need a click handler on the label to stop it.
   * @param event The click on the content.
   * @param input The native checkbox.
   * @returns void
   */
  public onContentClick(event: MouseEvent, input: HTMLInputElement): void {
    const label = (event.target as Element | null)?.closest('label');
    if (label?.control === input) return;
    input.click();
  }

  /**
   * Writes a new value to the input.
   * @param value The new value to be written.
   * @returns void
   */
  /**
   * `writeValue` é chamado pelo Angular a partir de qualquer contexto, incluindo
   * durante a deteção de mudanças (o consumer reativo de `refreshView` cobre o
   * template e todos os hooks de ciclo de vida). Escrever num signal aí lança
   * NG0600 e a escrita perde-se — o controlo fica com o valor novo e o input
   * desenhado com o antigo. `untracked` liberta a escrita e evita registar
   * dependências que este método não deve ter.
   */
  public writeValue(value: boolean): void {
    untracked(() => {
      this.value.set(value);

      // Chamado de dentro do próprio `valueChange` para recusar uma marcação (ex.: a
      // permissão crítica que espera confirmação no editor de Funções), o `set` acima
      // devolve `value()` ao que já estava renderizado — o `[checked]` não muda e o
      // Angular não escreve no DOM. O clique do usuário já tinha marcado o input
      // nativo, então a caixa ficava aparecendo marcada com o dado desmarcado, e o
      // que se salvava não era o que se via.
      const input = this.nativeInput()?.nativeElement;
      if (input) input.checked = !!value;
    });
  }

  /**
   * Registers a function to be called when the input changes.
   * @param fn The function to be called when the input changes.
   * @returns void
   */
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Registers a function to be called when the input is touched.
   * @param fn The function to be called when the input is touched.
   * @returns void
   */
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Updates the state of the input when it changes.
   * @param isDisabled A flag indicating if the input is disabled.
   * @returns void
   */
  public setDisabledState?(isDisabled: boolean): void {
    this.state.set(isDisabled ? 'disabled' : 'default');
  }
}
